import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { 
  ArrowLeft, 
  MapPin,
  Loader2,
  Home,
  Briefcase,
  Building,
  X,
  Plus,
  Navigation,
  Check,
  AlertTriangle
} from "lucide-react";
import axios from "axios";
import OrderPausedModal from "../components/OrderPausedModal";
import SuspensionBanner from "../components/SuspensionBanner";
import { getBackendUrl } from "../utils/api";

const API = `${getBackendUrl()}/api`;

const CheckoutPage = () => {
  const navigate = useNavigate();
  const [cart, setCart] = useState(null);
  const [loading, setLoading] = useState(true);
  const [placing, setPlacing] = useState(false);
  const [user, setUser] = useState(null);
  const [deliveryAddress, setDeliveryAddress] = useState("");
  const [deliveryLocation, setDeliveryLocation] = useState(null);
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [addresses, setAddresses] = useState([]);
  const [showNewAddressForm, setShowNewAddressForm] = useState(false);
  const [newAddress, setNewAddress] = useState({
    label: "Home",
    address_line1: "",
    address_line2: "",
    address_line3: "",
    pincode: ""
  });
  const [addressError, setAddressError] = useState("");
  const [showWaitlistPopup, setShowWaitlistPopup] = useState(false);
  const [waitlistEmail, setWaitlistEmail] = useState("");
  const [isGpsLocation, setIsGpsLocation] = useState(false);
  const [showOrderPausedModal, setShowOrderPausedModal] = useState(false);
  const [orderPausedData, setOrderPausedData] = useState({ message: null, resumeAt: null });
  const [isSuspended, setIsSuspended] = useState(false);
  const [suspensionMessage, setSuspensionMessage] = useState(null);

  useEffect(() => {
    const storedUser = localStorage.getItem("fleety_user");
    if (!storedUser) {
      navigate("/login");
      return;
    }
    const userData = JSON.parse(storedUser);
    setUser(userData);
    
    // Check user suspension status
    const checkUserStatus = async () => {
      try {
        const response = await axios.get(`${API}/user/status/${userData.id}`);
        if (response.data.is_suspended) {
          setIsSuspended(true);
          setSuspensionMessage(response.data.message);
        }
      } catch (error) {
        console.error("Error checking user status:", error);
        // Check from stored user data as fallback
        if (userData.is_suspended || userData.status === "suspended") {
          setIsSuspended(true);
          setSuspensionMessage("Your account has been suspended. You cannot place orders. Please contact Fleety support.");
        }
      }
    };
    
    checkUserStatus();
    
    // Get delivery address from localStorage
    const savedAddress = localStorage.getItem("fleety_delivery_address");
    if (savedAddress) {
      const address = JSON.parse(savedAddress);
      if (address.type === "gps") {
        setIsGpsLocation(true);
        setDeliveryAddress("📍 Current Location - Please select delivery address");
        setDeliveryLocation({ lat: address.lat, lng: address.lng });
        // Show address modal immediately for GPS users
        setShowAddressModal(true);
      } else {
        setIsGpsLocation(false);
        setDeliveryAddress(`${address.label || "Home"} - ${address.address_line1}, ${address.address_line2 || ""}, ${address.address_line3 || ""}, ${address.pincode}`);
        setDeliveryLocation({ pincode: address.pincode });
      }
    } else {
      setDeliveryAddress(`${userData.label || "Home"} - ${userData.address_line1}, ${userData.address_line2 || ""}, ${userData.address_line3 || ""}, ${userData.pincode}`);
      setDeliveryLocation({ pincode: userData.pincode });
    }
  }, [navigate]);

  useEffect(() => {
    if (user) {
      fetchCart();
      fetchAddresses();
    }
  }, [user, deliveryLocation]);

  const fetchCart = async () => {
    try {
      let params = {};
      
      // Get full address for accurate distance calculation
      const savedAddress = localStorage.getItem("fleety_delivery_address");
      if (savedAddress) {
        const address = JSON.parse(savedAddress);
        if (address.lat && address.lng) {
          // GPS coordinates
          params = { lat: address.lat, lng: address.lng };
        } else if (address.address_line1) {
          // Full address - send for geocoding
          const fullAddress = `${address.address_line1}, ${address.address_line2 || ''}, ${address.pincode}`;
          params = { address: fullAddress, pincode: address.pincode };
        } else if (address.pincode) {
          params = { pincode: address.pincode };
        }
      } else if (user?.pincode) {
        params = { pincode: user.pincode };
      }
      
      const response = await axios.get(`${API}/cart/${user.id}`, { params });
      if (!response.data.items?.length) {
        navigate("/cart");
        return;
      }
      setCart(response.data);
    } catch (error) {
      console.error("Error fetching cart:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAddresses = async () => {
    try {
      const response = await axios.get(`${API}/addresses/${user.id}`);
      setAddresses(response.data);
    } catch (error) {
      console.error("Error fetching addresses:", error);
    }
  };

  const handleSelectAddress = (address) => {
    // Check pincode
    if (address.pincode !== "560075") {
      setShowWaitlistPopup(true);
      return;
    }
    
    setDeliveryAddress(`${address.label || "Home"} - ${address.address_line1}, ${address.address_line2 || ""}, ${address.address_line3 || ""}, ${address.pincode}`);
    setDeliveryLocation({ pincode: address.pincode });
    setIsGpsLocation(false);
    
    // Save to localStorage
    localStorage.setItem("fleety_delivery_address", JSON.stringify({ type: "saved", ...address }));
    
    setShowAddressModal(false);
  };

  const handleAddNewAddress = async () => {
    setAddressError("");
    
    if (!newAddress.address_line1 || !newAddress.pincode) {
      setAddressError("Please fill in address and pincode");
      return;
    }
    
    // Check pincode
    if (newAddress.pincode !== "560075") {
      setShowWaitlistPopup(true);
      return;
    }
    
    try {
      await axios.post(`${API}/addresses`, {
        user_id: user.id,
        ...newAddress
      });
      
      // Refresh addresses and select the new one
      await fetchAddresses();
      handleSelectAddress(newAddress);
      setShowNewAddressForm(false);
      setNewAddress({ label: "Home", address_line1: "", address_line2: "", address_line3: "", pincode: "" });
    } catch (error) {
      setAddressError(error.response?.data?.detail || "Failed to add address");
    }
  };

  const handleWaitlistSubmit = async () => {
    if (!waitlistEmail) return;
    
    try {
      await axios.post(`${API}/waitlist`, { email: waitlistEmail, pincode: newAddress.pincode || "other" });
      alert("Thank you! We'll notify you when we start serving your area.");
      setShowWaitlistPopup(false);
      setWaitlistEmail("");
    } catch (error) {
      console.error("Error submitting waitlist:", error);
    }
  };

  const getDeliveryAddress = () => {
    return deliveryAddress;
  };

  const getLabelIcon = (label) => {
    if (!label) return Home;
    const lowerLabel = label.toLowerCase();
    if (lowerLabel.includes("home")) return Home;
    if (lowerLabel.includes("office") || lowerLabel.includes("work")) return Briefcase;
    return Building;
  };

  const placeOrder = async () => {
    setPlacing(true);
    try {
      // Check if Fleety (Food) orders are being accepted
      const statusResponse = await axios.get(`${API}/order-status`);
      if (!statusResponse.data.orders_accepted) {
        setOrderPausedData({
          message: statusResponse.data.message,
          resumeAt: statusResponse.data.resume_at
        });
        setShowOrderPausedModal(true);
        setPlacing(false);
        return;
      }

      console.log("Starting order placement...");
      
      // Get delivery coordinates from saved address
      let deliveryLat = null;
      let deliveryLng = null;
      const savedAddress = localStorage.getItem("fleety_delivery_address");
      if (savedAddress) {
        const address = JSON.parse(savedAddress);
        if (address.lat && address.lng) {
          deliveryLat = address.lat;
          deliveryLng = address.lng;
        }
      }
      
      // Step 1: Create order in database first
      const orderResponse = await axios.post(`${API}/orders`, {
        user_id: user.id,
        restaurant_id: cart.restaurant_id,
        items: cart.items,
        // New GST structure fields
        item_total: cart.item_total,
        packing_charges: cart.packing_charges,
        subtotal: cart.subtotal,
        food_gst: cart.food_gst,
        delivery_fee: cart.delivery_fee,
        platform_fee: cart.platform_fee,
        service_gst: cart.service_gst,
        gst: cart.gst,
        total: cart.total,
        payment_method: "cashfree",
        delivery_address: getDeliveryAddress(),
        delivery_lat: deliveryLat,
        delivery_lng: deliveryLng
      });
      
      console.log("Order created:", orderResponse.data);
      
      if (!orderResponse.data.success) {
        throw new Error("Failed to create order");
      }

      const orderId = orderResponse.data.order_id;
      const orderNumber = orderResponse.data.order_number;
      const finalPricing = orderResponse.data.pricing || cart;
      setCart((current) => ({ ...current, ...finalPricing }));

      // Step 2: Create Cashfree payment order with Easy Split
      console.log("Creating Cashfree payment order with Easy Split...");
      const paymentResponse = await axios.post(`${API}/payment/create-order`, {
        order_id: `FLEETY_${orderNumber}`,
        order_amount: finalPricing.total,
        customer_name: user.full_name || "Customer",
        customer_email: user.email || "customer@fleety.com",
        customer_phone: user.phone_number,
        return_url: `${window.location.origin}/payment-status/${orderId}`,
        // Easy Split parameters - restaurant gets subtotal + food_gst
        restaurant_id: cart.restaurant_id,
        subtotal: finalPricing.subtotal,
        food_gst: finalPricing.food_gst
      });

      console.log("Payment order created:", paymentResponse.data);

      if (paymentResponse.data.success && paymentResponse.data.payment_session_id) {
        const paymentSessionId = paymentResponse.data.payment_session_id;
        
        // Use Cashfree SDK for checkout (recommended approach for production)
        if (window.Cashfree) {
          const cashfree = window.Cashfree({
            mode: "production"  // Production mode for live payments
          });
          
          console.log("Opening Cashfree checkout with session:", paymentSessionId);
          
          cashfree.checkout({
            paymentSessionId: paymentSessionId,
            redirectTarget: "_self"  // Redirect in same window
          }).then((result) => {
            console.log("Cashfree checkout result:", result);
            if (result.error) {
              console.error("Payment error:", result.error);
              alert("Payment failed: " + result.error.message);
              setPlacing(false);
            }
            // On success, user will be redirected to return_url
          }).catch((error) => {
            console.error("Cashfree checkout error:", error);
            alert("Payment failed. Please try again.");
            setPlacing(false);
          });
        } else {
          // Fallback: Direct redirect if SDK not loaded
          console.warn("Cashfree SDK not loaded, using direct redirect");
          const paymentLink = paymentResponse.data.payment_link;
          if (paymentLink) {
            window.location.href = paymentLink;
          } else {
            throw new Error("No payment link available");
          }
        }
      } else {
        throw new Error("Failed to create payment session");
      }
    } catch (error) {
      console.error("Error placing order:", error);
      if (
        error.response?.status === 409 &&
        error.response?.data?.detail?.code === "PRICING_CHANGED"
      ) {
        const updatedPricing = error.response.data.detail.pricing;
        setCart((current) => ({ ...current, ...updatedPricing }));
        alert(error.response.data.detail.message);
        setPlacing(false);
        return;
      }
      alert("Failed to place order. Please try again.");
      setPlacing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#3FA9F5] border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="checkout-page">
      {/* Suspension Banner - shown when account is suspended */}
      {isSuspended && <SuspensionBanner message={suspensionMessage} />}
      
      {/* Top Spacer for mobile */}
      <div className="h-6 bg-white"></div>
      
      {/* Header */}
      <header className="bg-white px-4 py-4 shadow-sm sticky top-0 z-40">
        <div className="max-w-3xl mx-auto flex items-center gap-4">
          <button 
            onClick={() => navigate(-1)}
            className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100"
            data-testid="back-btn"
          >
            <ArrowLeft size={24} className="text-gray-700" />
          </button>
          <h1 className="text-xl font-bold text-gray-900">Checkout</h1>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 py-6">
        {/* Suspension Block Message */}
        {isSuspended && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-4" data-testid="suspension-block">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-6 h-6 text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-red-800">Cannot Place Order</p>
                <p className="text-sm text-red-600 mt-1">
                  Your account has been suspended. You cannot place orders at this time. Please contact Fleety support for assistance.
                </p>
              </div>
            </div>
          </div>
        )}
        
        {/* Delivery Address */}
        <div className="bg-white rounded-xl p-4 shadow-sm mb-4" data-testid="delivery-address">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-[#3FA9F5]/10 rounded-full flex items-center justify-center">
              <MapPin size={20} className="text-[#3FA9F5]" />
            </div>
            <div className="flex-1">
              <p className="text-sm text-gray-500">Delivering to</p>
              <p className="font-medium text-gray-900">{getDeliveryAddress()}</p>
              {isGpsLocation && (
                <p className="text-xs text-orange-500 mt-1">⚠️ Please select a delivery address</p>
              )}
            </div>
            <button 
              onClick={() => setShowAddressModal(true)}
              className="text-[#3FA9F5] text-sm font-medium hover:underline"
            >
              Change
            </button>
          </div>
        </div>

        {/* Order Summary */}
        <div className="bg-white rounded-xl shadow-sm mb-4" data-testid="order-summary">
          <div className="p-4 border-b border-gray-100">
            <h2 className="font-semibold text-gray-900">Order Summary</h2>
            <p className="text-sm text-gray-500">{cart?.restaurant_name}</p>
          </div>
          <div className="p-4">
            {cart?.items?.map((item) => (
              <div key={item.item_id} className="flex justify-between py-2 text-sm">
                <span className="text-gray-600">{item.name} × {item.quantity}</span>
                <span className="font-medium">₹{item.price * item.quantity}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Bill Details */}
        <div className="bg-white rounded-xl shadow-sm p-4 mb-6" data-testid="bill-details">
          <h2 className="font-semibold text-gray-900 mb-4">Bill Details</h2>
          <div className="space-y-3 text-sm">
            {/* Food Section */}
            <div className="flex justify-between">
              <span className="text-gray-600">Item Total</span>
              <span className="font-medium">₹{cart?.item_total?.toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Packing Charges</span>
              <span className="font-medium">₹{cart?.packing_charges?.toFixed(2)}</span>
            </div>
            <div className="flex justify-between border-t border-gray-100 pt-2">
              <span className="text-gray-700 font-medium">Subtotal (Food + Packing)</span>
              <span className="font-medium">₹{cart?.subtotal?.toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">GST on Food & Packing (5%)</span>
              <span className="font-medium">₹{cart?.food_gst?.toFixed(2)}</span>
            </div>
            
            {/* Delivery & Platform Section */}
            <div className="border-t border-gray-100 pt-2"></div>
            <div className="flex justify-between">
              <span className="text-gray-600">Delivery Fee ({cart?.distance?.toFixed(1) || 0} km)</span>
              <span className="font-medium">₹{cart?.delivery_fee?.toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Platform Fee</span>
              <span className="font-medium">₹{cart?.platform_fee?.toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">GST on Delivery & Platform (18%)</span>
              <span className="font-medium">₹{cart?.service_gst?.toFixed(2)}</span>
            </div>
            
            {/* Total */}
            <div className="border-t border-gray-200 pt-3 flex justify-between">
              <span className="font-semibold text-gray-900">To Pay</span>
              <span className="font-bold text-lg text-gray-900">₹{cart?.total?.toFixed(2)}</span>
            </div>
          </div>
        </div>

        {/* Proceed to Payment Button */}
        <Button
          onClick={() => {
            if (isSuspended) {
              return; // Block for suspended users
            }
            if (isGpsLocation) {
              setShowAddressModal(true);
              return;
            }
            placeOrder();
          }}
          disabled={placing || isSuspended}
          className={`w-full h-14 text-white text-lg font-semibold rounded-xl shadow-lg disabled:opacity-50 ${
            isSuspended 
              ? 'bg-gray-400 cursor-not-allowed' 
              : 'bg-[#22C55E] hover:bg-[#16A34A]'
          }`}
          data-testid="proceed-payment-btn"
        >
          {placing ? (
            <Loader2 className="w-6 h-6 animate-spin" />
          ) : isSuspended ? (
            "Account Suspended"
          ) : (
            `Proceed to Payment • ₹${cart?.total}`
          )}
        </Button>
      </div>

      {/* Address Selection Modal */}
      {showAddressModal && (
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50" onClick={() => !isGpsLocation && setShowAddressModal(false)}>
          <div className="bg-white w-full max-w-md rounded-t-2xl sm:rounded-2xl max-h-[80vh] overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="p-4 border-b border-gray-100 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">
                {isGpsLocation ? "Select Delivery Address" : "Change Delivery Address"}
              </h2>
              {!isGpsLocation && (
                <button onClick={() => setShowAddressModal(false)} className="p-2 hover:bg-gray-100 rounded-full">
                  <X size={20} className="text-gray-500" />
                </button>
              )}
            </div>
            
            {isGpsLocation && (
              <div className="px-4 py-3 bg-orange-50 border-b border-orange-100">
                <p className="text-sm text-orange-700">
                  <Navigation size={14} className="inline mr-1" />
                  You are using your current location. Please select or add a delivery address to proceed.
                </p>
              </div>
            )}
            
            <div className="overflow-y-auto max-h-[50vh]">
              {/* Saved Addresses */}
              {addresses.length > 0 && (
                <div className="p-4 border-b border-gray-100">
                  <p className="text-sm text-gray-500 mb-3">Saved Addresses</p>
                  {addresses.map((address) => {
                    const IconComponent = getLabelIcon(address.label);
                    return (
                      <button
                        key={address.id}
                        onClick={() => handleSelectAddress(address)}
                        className="w-full p-3 flex items-start gap-3 hover:bg-gray-50 rounded-lg transition-colors text-left mb-2"
                      >
                        <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <IconComponent size={18} className="text-gray-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900">{address.label}</p>
                          <p className="text-sm text-gray-500 truncate">{address.address_line1}</p>
                          <p className="text-xs text-gray-400">{address.pincode}</p>
                        </div>
                        {address.pincode === "560075" ? (
                          <Check size={20} className="text-green-500 flex-shrink-0" />
                        ) : (
                          <span className="text-xs text-orange-500">Not serviceable</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
              
              {/* Profile Address */}
              {user && (
                <div className="p-4 border-b border-gray-100">
                  <p className="text-sm text-gray-500 mb-3">Profile Address</p>
                  <button
                    onClick={() => handleSelectAddress({
                      label: user.label || "Home",
                      address_line1: user.address_line1,
                      address_line2: user.address_line2,
                      address_line3: user.address_line3,
                      pincode: user.pincode
                    })}
                    className="w-full p-3 flex items-start gap-3 hover:bg-gray-50 rounded-lg transition-colors text-left"
                  >
                    <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <Home size={18} className="text-gray-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900">{user.label || "Home"}</p>
                      <p className="text-sm text-gray-500 truncate">{user.address_line1}</p>
                      <p className="text-xs text-gray-400">{user.pincode}</p>
                    </div>
                    {user.pincode === "560075" ? (
                      <Check size={20} className="text-green-500 flex-shrink-0" />
                    ) : (
                      <span className="text-xs text-orange-500">Not serviceable</span>
                    )}
                  </button>
                </div>
              )}
              
              {/* Add New Address */}
              {!showNewAddressForm ? (
                <div className="p-4">
                  <button
                    onClick={() => setShowNewAddressForm(true)}
                    className="w-full p-3 flex items-center gap-3 border-2 border-dashed border-gray-200 rounded-lg hover:border-[#3FA9F5] transition-colors"
                  >
                    <div className="w-10 h-10 bg-[#3FA9F5]/10 rounded-full flex items-center justify-center">
                      <Plus size={18} className="text-[#3FA9F5]" />
                    </div>
                    <span className="font-medium text-[#3FA9F5]">Add New Address</span>
                  </button>
                </div>
              ) : (
                <div className="p-4 space-y-4">
                  <p className="text-sm font-medium text-gray-700">New Address</p>
                  
                  {/* Label Selection */}
                  <div className="flex gap-2">
                    {["Home", "Office", "Other"].map((label) => (
                      <button
                        key={label}
                        onClick={() => setNewAddress(prev => ({ ...prev, label }))}
                        className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                          newAddress.label === label 
                            ? 'bg-[#3FA9F5] text-white' 
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                  
                  <Input
                    placeholder="Address Line 1 *"
                    value={newAddress.address_line1}
                    onChange={(e) => setNewAddress(prev => ({ ...prev, address_line1: e.target.value }))}
                    className="h-12"
                  />
                  <Input
                    placeholder="Address Line 2"
                    value={newAddress.address_line2}
                    onChange={(e) => setNewAddress(prev => ({ ...prev, address_line2: e.target.value }))}
                    className="h-12"
                  />
                  <Input
                    placeholder="Landmark / Area"
                    value={newAddress.address_line3}
                    onChange={(e) => setNewAddress(prev => ({ ...prev, address_line3: e.target.value }))}
                    className="h-12"
                  />
                  <Input
                    placeholder="Pincode *"
                    value={newAddress.pincode}
                    onChange={(e) => setNewAddress(prev => ({ ...prev, pincode: e.target.value }))}
                    className="h-12"
                    maxLength={6}
                  />
                  
                  {addressError && (
                    <p className="text-sm text-red-500">{addressError}</p>
                  )}
                  
                  <div className="flex gap-3">
                    <Button
                      onClick={() => setShowNewAddressForm(false)}
                      variant="outline"
                      className="flex-1 h-12"
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleAddNewAddress}
                      className="flex-1 h-12 bg-[#3FA9F5] hover:bg-[#2196F3]"
                    >
                      Save & Use
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Waitlist Popup */}
      {showWaitlistPopup && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full animate-slideUp">
            <div className="text-center mb-4">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <MapPin size={32} className="text-orange-500" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Coming Soon!</h3>
              <p className="text-gray-600 text-sm">
                Thank you for your love! We are not serving in this area yet. Please provide your email and we will notify you as soon as we go live.
              </p>
            </div>
            
            <Input
              type="email"
              placeholder="Enter your email"
              value={waitlistEmail}
              onChange={(e) => setWaitlistEmail(e.target.value)}
              className="h-12 mb-4"
            />
            
            <Button
              onClick={handleWaitlistSubmit}
              disabled={!waitlistEmail}
              className={`w-full h-12 font-semibold ${
                waitlistEmail 
                  ? 'bg-green-500 hover:bg-green-600 text-white' 
                  : 'bg-gray-200 text-gray-500'
              }`}
            >
              Notify Me
            </Button>
            
            <button
              onClick={() => setShowWaitlistPopup(false)}
              className="w-full mt-3 text-gray-500 text-sm hover:underline"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Order Paused Modal */}
      <OrderPausedModal
        isOpen={showOrderPausedModal}
        onClose={() => setShowOrderPausedModal(false)}
        message={orderPausedData.message}
        resumeAt={orderPausedData.resumeAt}
        onBrowseRestaurants={() => {
          setShowOrderPausedModal(false);
          navigate("/home");
        }}
      />
    </div>
  );
};

export default CheckoutPage;
